// packages/ui-kit/src/primitives/SegmentedControl.tsx
// C3F v18 — reference: design/system/components/forms/SegmentedControl.{jsx,prompt.md}
// Single choice inline, 2-4 short options. For a vertical list use Radio.
import type { ReactNode } from "react";

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface Props<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Accessible name for the group (it is an ARIA radiogroup). */
  ariaLabel?: string;
  /** `md` is the DS spec (36px segments); `sm` is a denser caption variant. */
  size?: "sm" | "md";
  fullWidth?: boolean;
  className?: string;
}

/**
 * Single-select pill group — a pill-shaped bg-subtle rail whose active
 * segment inverts (fg-default fill, fg-inverse text). Rendered as a proper
 * ARIA radiogroup so keyboard + screen-reader users get single-choice
 * semantics.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
  size = "md",
  fullWidth = false,
  className = "",
}: Props<T>) {
  // 44px touch floor on phones; DS desktop segment height is 36px.
  const sizeCls =
    size === "sm"
      ? "min-h-touch sm:min-h-9 px-3 text-caption"
      : "min-h-touch sm:min-h-9 px-4 text-sm";
  return (
    <div
      role="radiogroup"
      {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      className={`${fullWidth ? "flex w-full" : "inline-flex"} gap-0.5 rounded-pill bg-subtle p-1 ${className}`}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={`${fullWidth ? "flex-1" : "flex-none"} cursor-pointer whitespace-nowrap rounded-pill font-sans font-semibold transition-colors duration-fast ease-out focus-visible:outline-none focus-visible:shadow-focus ${sizeCls} ${
              active
                ? "bg-fg-default text-fg-inverse"
                : "text-fg-muted hover:bg-hover active:bg-active"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
